import ClearIcon from './ClearIcon'
import type { MouseEvent, ReactElement, ReactNode } from 'react'
import { modelModeLabel } from '../utils/modelModeLabel'
import { useTranslation } from 'react-i18next'
import { Coin } from '../../../icons/Coin'
import { Icon } from '../../../../public/icons/Icon.tsx'
import ModelIcon from '../../../components/ModelIcon'
import SideTooltip from '../../../components/SideTooltip'

interface ModelBadgeProps {
	modelName: string
	/** Иконка-логотип нейронки (model.icon). Шапка показывает ТОЛЬКО лого
	 *  (Artlist-style): имя модели живёт в селекте моделей, режим («Text to
	 *  Image») и описание — в тултипе при ховере на лого. */
	modelIcon?: string
	/** Цена или «…», пока сервер меряет видео на входе (плата за секунды входного видео). */
	currentCost: number | string
	isVideo: boolean
	requiresVideo: boolean
	requiresImage: boolean
	/** Модель опционально принимает изображение (загрузка не обязательна) */
	supportsImageInput?: boolean
	isAudio?: boolean
	isPresentation?: boolean
	clearDisabled: boolean
	onClear: () => void
	costClassName?: string
	costCoinLast?: boolean
	wrapClearButton?: (btn: ReactElement) => ReactElement
	onHelpClick?: (e: MouseEvent<HTMLButtonElement>) => void
	/** Кнопка-искра «Придумать идею» (Идея-мастер) рядом с «?». Не передан — не рендерится. */
	onIdeaClick?: (e: MouseEvent<HTMLButtonElement>) => void
	/** История генераций — иконка-часики в шапке (замена мобильной пилюли
	 *  над виджетом). Не передан — не рендерится (виджеты /bots без неё). */
	onHistoryClick?: () => void
	extraActions?: ReactNode
	/** Минимальная шапка (tool-режим): только цена + управление. Прячет иконку
	 *  типа, имя нейросети и лейбл режима — карточка показывает свой заголовок. */
	compact?: boolean
	/** Слот ПЕРЕД лого (Artlist: свёрнутый виджет с карточкой показывает
	 *  аватар+название карточки первой позицией бара). */
	leadSlot?: ReactNode
}


export function ModelBadge({
	modelName,
	modelIcon,
	currentCost,
	isVideo,
	requiresVideo,
	requiresImage,
	supportsImageInput,
	isAudio,
	isPresentation,
	clearDisabled,
	onClear,
	costClassName = 'generate-model-badge__cost',
	costCoinLast = false,
	wrapClearButton,
	onHelpClick,
	onIdeaClick,
	onHistoryClick,
	extraActions,
	compact,
	leadSlot,
}: ModelBadgeProps) {
	const { t } = useTranslation()

	const detectedAudio = isAudio || modelName.toLowerCase().includes('suno')
	const detectedPresentation = isPresentation || modelName.toLowerCase().includes('slide') || modelName.toLowerCase().includes('umnik-slides')

	const typeLabel = modelModeLabel({ modelName, isVideo, requiresVideo, requiresImage, isAudio, isPresentation })

	const modeTooltip = detectedAudio
		? t('generate.modeTooltip.music')
		: detectedPresentation
			? t('generate.modeTooltip.slides')
			: isVideo
				? requiresVideo
					? requiresImage
						? t('generate.modeTooltip.videoToVideo')
						: t('generate.modeTooltip.videoOnly')
					: requiresImage
						? t('generate.modeTooltip.imageToVideo')
						: supportsImageInput
							? t('generate.modeTooltip.optionalImage')
							: t('generate.modeTooltip.textToVideo')
				: requiresImage
					? t('generate.modeTooltip.imageToImage')
					: supportsImageInput
						? t('generate.modeTooltip.optionalImage')
						: t('generate.modeTooltip.textToImage')

	const clearBtn: ReactElement = (
		<button
			className="gw__clear-btn"
			onClick={onClear}
			type="button"
			aria-label={t('generate.widget.clear')}
			title={wrapClearButton ? undefined : t('generate.widget.clear')}
			disabled={clearDisabled}
		>
			<ClearIcon />
		</button>
	)

	return (
		<div className={`generate-model-badge${compact ? ' generate-model-badge--compact' : ''}${leadSlot ? ' generate-model-badge--with-lead' : ''}`}>
			{leadSlot}
			{/* ТОЛЬКО логотип нейронки (Artlist-style): имя модели живёт в
			    селекте моделей, режим («Text to Image») и его описание — в
			    тултипе на лого. voice-flash подсвечивает лого (см. _widget). */}
			<SideTooltip text={`${typeLabel}: ${modeTooltip}`} preferSide="auto">
				<span
					className="generate-model-badge__logo"
					aria-label={`${modelName} — ${typeLabel}`}
					role="img"
				>
					{modelIcon ? (
						<ModelIcon icon={modelIcon} name={modelName} size={16} />
					) : (
						<span className="generate-model-badge__logo-fallback" aria-hidden="true">
							{modelName.charAt(0).toUpperCase()}
						</span>
					)}
				</span>
			</SideTooltip>

			<span className={costClassName}>
				{costCoinLast ? (
					<>
						{currentCost}
						<Coin width={13} height={13} style={{ marginLeft: 2, verticalAlign: 'middle' }} />
					</>
				) : (
					<>
						<Coin width={13} height={13} />
						{currentCost}
					</>
				)}
			</span>

			{onIdeaClick && (
				<button
					type="button"
					className="generate-model-badge__idea"
					onClick={onIdeaClick}
					aria-label={t('generate.ideaButton')}
					title={t('generate.ideaButton')}
				>
					<Icon id="gen-idea" size={11} />
					{/* Текст виден на широкой шапке; на узких/мобиле остаётся кружок-искра */}
					<span className="generate-model-badge__idea-label">{t('generate.ideaButton')}</span>
				</button>
			)}

			{/* Персонаж из шапки убран (решение владельца 2026-08-01): капсула-
			    трансформер живёт в РЯДУ слотов рядом с загрузкой фото — и вход,
			    и статус выбранного в одном месте, шапка разгружена. Ряд слотов
			    теперь виден на всех ширинах (см. .gm__uploads в _layout.scss). */}
			{/* Правый блок управления — ВСЕГДА прижат вправо (margin-left:auto
			    на контейнере, а не на «?»: в tool-режиме «?» отсутствует и
			    раньше весь ряд слипался влево). */}
			<span className="generate-model-badge__right">
				{onHistoryClick && (
					<button
						type="button"
						className="generate-model-badge__history"
						onClick={onHistoryClick}
						aria-label="История генераций"
						title="История"
					>
						<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<circle cx="12" cy="12" r="9" />
							<path d="M12 7v5l3 2" />
						</svg>
					</button>
				)}
				{onHelpClick && (
					<button
						type="button"
						className="generate-model-badge__help"
						onClick={onHelpClick}
						aria-label={t('generate.howToUse')}
						title={t('generate.howToUse')}
					>
						?
					</button>
				)}

				{wrapClearButton ? wrapClearButton(clearBtn) : clearBtn}

				{extraActions}
			</span>
		</div>
	)
}
